import LeakLeftContainer from "./LeakLeftContainer";
import { useAuth } from "../../../../AuthContext";
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import "../../../static/styles/leak.css";
import URLS from "../../../urls";
import { useTheme, Box } from "@mui/material";
import LeakMap from "./LeakMap";
import Loading from "../../components/Loading/Loading";
import { parseLeaksToMarkers } from "../components/markerMaker";

function Leak() {
  const theme = useTheme();
  let { leakId } = useParams();
  const { axiosInstance, userProfile } = useAuth();
  const [leakData, setLeakData] = useState([]);
  const [cleanLeakData, setCleanLeakData] = useState(null);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [leakSelected, setLeakSelected] = useState(() => {
    const savedFilters = sessionStorage.getItem("selectedFilters");
    if (savedFilters) {
      const parsedFilters = JSON.parse(savedFilters);
      return parsedFilters.leak || { id: 0 };
    }
    return { id: 0 };
  });
  const [AOIGeoJSON, setAOIGeoJSON] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [pipes, setPipes] = useState(null);
  const [markersData, setMarkersData] = useState([]);
  const [cleanMarkersData, setCleanMarkersData] = useState([]);
  const [customAOI, setCustomAOI] = useState(() => {
    const savedAOI = sessionStorage.getItem("customAOI");
    return savedAOI ? savedAOI : null;
  });

  const [severityFilter, setSeverityFilter] = useState(null);
  const [filterCleared, setFilterCleared] = useState(false);

  const onDrawingToggle = (isDrawing) => {
    setIsDrawing(isDrawing);
  };

  const onLeakSelected = (selected) => {
    setFilterCleared(false);
    setLeakSelected(selected);
  };

  const fetchData = async () => {
    setIsInitialLoading(true);
    let leak_url = URLS.LEAK.MAIN;
    if (leakId) {
      leak_url += "/" + leakId;
    }
    try {
      const [leakResponse, AOIResponse, pipeResponse, customMarkersResponse] =
        await Promise.all([
          axiosInstance.get(
            leak_url +
              "?fields=id,identifier,start_date,end_date,position,leak_status,leak_severity"
          ),
          axiosInstance.get(URLS.AOI.MAIN),
          axiosInstance.get(URLS.PIPE.MAIN),
          axiosInstance.get(URLS.LEAK.IDS_AND_POSITION),
        ]);
      setPipes(pipeResponse.data.features);
      setAOIGeoJSON(AOIResponse.data.features);
      parseLeakData(leakResponse.data.results, customMarkersResponse.data.features);
    } catch (error) {
      console.error("Hubo un error al realizar las llamadas a la API:", error);
    } finally {
      setIsInitialLoading(false);
    }
  };
  
  const parseMarkersData = (markers) => {
    return parseLeaksToMarkers(markers, theme, leakSelected?.id);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchLeakData = async (filter, clear = false) => {
    setIsLoading(true);
    try {
      let query = "";
      if (!clear) {
        const filterParts = [];
        if (filter) {
          filterParts.push(filter);
        }
        if (customAOI && customAOI !== "null") {
          filterParts.push(`geom=${encodeURIComponent(customAOI)}`);
        }
        query = filterParts.join("&");
        setFilterCleared(false);
      } else {
        setFilterCleared(true);
      }

      const fullQuery = query ? `?${query}` : "";
      const leakResponse = await axiosInstance.get(
        `${URLS.LEAK.MAIN}${fullQuery}`
      );
      const customMarkersResponse = await axiosInstance.get(
        `${URLS.LEAK.IDS_AND_POSITION}${fullQuery}`
      );
      if (leakResponse.data) {
        parseLeakData(leakResponse.data.results, customMarkersResponse.data.features);
      }
    } catch (error) {
      console.error("Hubo un error al realizar las llamadas a la API:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const parseLeakData = (leakResponse, markersData) => {
    let leaks = leakResponse;
    let selected = leakSelected;
    if (!leakId) {
      leaks = leakResponse.sort((a, b) => {
        const dateA = new Date(a.start_date);
        const dateB = new Date(b.start_date);
        return dateB - dateA;
      });
      if (leakSelected) {
        selected = leaks.find((leak) => leak.id === leakSelected.id) || leaks[0];
      } else {
        selected = leaks[0];
      }
    }

    setLeakData(leaks);
    setCleanLeakData(leaks);
    setLeakSelected(selected);

    if (markersData && markersData.length > 0) {
      const parsedMarkers = parseMarkersData(markersData);
      setMarkersData(parsedMarkers);
      setCleanMarkersData(markersData);
    }
  };

  const filterLeakByPolygon = (data) => {
    setLeakData(data);
  };

  useEffect(() => {
    if (customAOI) {
      sessionStorage.setItem("customAOI", customAOI);
    }
  }, [customAOI]);

  useEffect(() => {
    if (
      severityFilter !== null &&
      severityFilter.length > 0 &&
      !severityFilter.includes(0)
    ) {
      const filteredLeakData = cleanLeakData.filter((leak) =>
        severityFilter.includes(leak.leak_severity)
      );
      setLeakData(filteredLeakData);

      const filteredMarkers = cleanMarkersData.filter((leak) =>
        severityFilter.includes(leak.properties.leak_severity)
      );
      const parsedMarkers = parseMarkersData(filteredMarkers);
      setMarkersData(parsedMarkers);
    } else {
      setLeakData(cleanLeakData);
      const parsedMarkers = parseMarkersData(cleanMarkersData);
      setMarkersData(parsedMarkers);
    }
  }, [severityFilter, cleanLeakData, cleanMarkersData]);

  const memoizedMarkersData = useMemo(() => markersData, [markersData]);

  return (
    <Box className="leak-app">
      {isInitialLoading || !userProfile?.profile.center ? (
        <Loading isLoading={isLoading} />
      ) : (
        <>
          {leakData && (
            <Box className="leak-container">
              <LeakLeftContainer
                leakData={leakData}
                leakSelected={leakSelected}
                onLeakSelected={onLeakSelected}
                onApplyFilters={fetchLeakData}
                onDrawingToggle={onDrawingToggle}
                isDrawing={isDrawing}
                setCustomAOI={setCustomAOI}
                setFilterCleared={setFilterCleared}
                isLoading={isLoading}
              />
              <LeakMap
                data={memoizedMarkersData}
                selected={leakSelected}
                AOIGeoJSON={AOIGeoJSON}
                filterLeakByPolygon={filterLeakByPolygon}
                setCustomAOI={setCustomAOI}
                customAOI={customAOI}
                isDrawing={isDrawing}
                onDrawingToggle={onDrawingToggle}
                pipesGeoJSON={pipes}
                setSeverityFilter={setSeverityFilter}
                filterCleared={filterCleared}
                setFilterCleared={setFilterCleared}
                isLoading={isLoading}
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
}

export default Leak;